import { useEffect, useMemo, useState } from 'react';
import { PencilSquareIcon, TrashIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import SportBadge from '@/components/ui/SportBadge';
import Dropdown from '@/components/ui/Dropdown';
import EmptyState from '@/components/ui/EmptyState';
import Pagination from '@/components/ui/Pagination';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { BulkDeleteBar, SelectAllHeader, SelectRowCell } from '@/components/ui/BulkDelete';
import { useRowSelection } from '@/lib/useRowSelection';
import { useSports, useThemesRef } from '@/features/reference/useReference';
import { deleteScalingSetting, type ScalingSetting } from '@/api/scalingService';
import { useScaling } from './useScaling';
import { roundingLabel } from './rounding';
import ScalingModal from './ScalingModal';
import { useScalableFormats } from './formatOptions';

const rowId = (r: ScalingSetting) => `${r.formatId}:${r.themeId}:${r.sportIdentifier}`;

const ScalingPage = () => {
  const [theme, setTheme] = useState('');
  const [sport, setSport] = useState('');
  const [format, setFormat] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [theme, sport, format]);

  const filters = useMemo(
    () => ({ theme: theme || undefined, sport: sport || undefined, format: format || undefined, page }),
    [theme, sport, format, page]
  );

  const { data: themes = [] } = useThemesRef();
  const { data: sports = [] } = useSports();
  const formats = useScalableFormats();
  const formatLabelOf = (r: ScalingSetting) =>
    formats.find((f) => f.id === r.formatId)?.optionLabel ?? r.formatLabel ?? r.formatIdentifier;
  const { items, total, pageSize, isLoading, createScaling, updateScaling, deleteScaling, isSaving, invalidate } =
    useScaling(filters);
  const selection = useRowSelection(items, rowId, filters);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ScalingSetting | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (row: ScalingSetting) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleDelete = (row: ScalingSetting) => {
    const combo = `${row.themeLabel} / ${row.sportIdentifier} / ${formatLabelOf(row)}`;
    if (
      window.confirm(
        `Supprimer le réglage « ${combo} » ? La combinaison reviendra au comportement sans scaling (facteur 1).`
      )
    ) {
      deleteScaling({ formatId: row.formatId, themeId: row.themeId, sportIdentifier: row.sportIdentifier });
    }
  };

  return (
    <>
      <PageHeader
        title="Réglages de scaling"
        description="Thématiques / Scaling — s'applique uniquement aux cycles où le scaling est activé"
        action={<Button onClick={openCreate}>+ Ajouter</Button>}
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Select label="Thème" value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="">Tous</option>
              {themes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-48">
            <Select label="Sport" value={sport} onChange={(e) => setSport(e.target.value)}>
              <option value="">Tous</option>
              {sports.map((s) => (
                <option key={s.identifier} value={s.identifier}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-52">
            <Select label="Format" value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="">Tous</option>
              {formats.map((f) => (
                <option key={f.id} value={f.identifier}>
                  {f.optionLabel}
                </option>
              ))}
            </Select>
          </div>
          <p className="ml-auto text-sm text-gray-500">{total} réglages</p>
        </div>
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={<AdjustmentsHorizontalIcon />}
          title="Aucun réglage"
          description="Chaque combinaison (thème, sport, format) sans réglage utilise un facteur de 1 (aucun scaling)."
          action={<Button onClick={openCreate}>+ Ajouter</Button>}
        />
      )}

      {!isLoading && items.length > 0 && (
        <Card className="p-0">
          <BulkDeleteBar
            selection={selection}
            labelOf={(r) => `${r.themeLabel ?? '—'} / ${r.sportIdentifier} / ${formatLabelOf(r)}`}
            remove={(r) => deleteScalingSetting(r.formatId, r.themeId, r.sportIdentifier)}
            onDone={invalidate}
          />
          <Table>
            <THead>
              <tr>
                <SelectAllHeader selection={selection} />
                <TH>Thème</TH>
                <TH>Sport</TH>
                <TH>Format</TH>
                <TH>Factor</TH>
                <TH>Arrondi</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((row) => (
                <TR key={`${row.formatId}-${row.themeId}-${row.sportIdentifier}`}>
                  <SelectRowCell selection={selection} row={row} />
                  <TD className="font-bold text-gray-900 dark:text-gray-100">{row.themeLabel ?? '—'}</TD>
                  <TD>
                    <SportBadge identifier={row.sportIdentifier} />
                  </TD>
                  <TD>
                    <Badge color="blue">{formatLabelOf(row)}</Badge>
                  </TD>
                  <TD className={row.factor === 1 ? 'text-gray-400' : 'font-medium text-gray-900 dark:text-gray-100'}>
                    ×{row.factor.toFixed(2)}
                  </TD>
                  <TD className="text-gray-500">{roundingLabel(row.roundingStep)}</TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Modifier',
                          icon: <PencilSquareIcon className="h-4 w-4" />,
                          onClick: () => openEdit(row),
                        },
                        {
                          label: 'Supprimer',
                          icon: <TrashIcon className="h-4 w-4" />,
                          destructive: true,
                          onClick: () => handleDelete(row),
                        },
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </Card>
      )}

      <ScalingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        setting={editing}
        isSaving={isSaving}
        onCreate={(payload) => createScaling(payload)}
        onUpdate={({ formatId, themeId, sportIdentifier, factor, roundingStep }) =>
          updateScaling({ formatId, themeId, sportIdentifier, payload: { factor, roundingStep } })
        }
      />
    </>
  );
};

export default ScalingPage;
